import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../../config/runtime';
import AppModal from '../common/AppModal';
import './KnowledgeManager.css';

const ENTRY_TYPE_OPTIONS = [
  { value: 'base_n1', label: 'Base N1' },
  { value: 'faq', label: 'FAQ' },
  { value: 'runbook', label: 'Runbook' },
  { value: 'procedimento', label: 'Procedimento' },
  { value: 'playbook', label: 'Playbook' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
];

const SOURCE_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'ia', label: 'IA' },
  { value: 'documentacao', label: 'Documentacao' },
  { value: 'importacao', label: 'Importacao' },
];

const VIEW_OPTIONS = [
  { id: 'artigos', label: 'Artigos' },
  { id: 'documentos', label: 'Documentacao' },
  { id: 'busca', label: 'Busca IA' },
];

const parseTags = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString('pt-BR');
};

const KnowledgeManager = () => {
  const [activeView, setActiveView] = useState('artigos');

  const [overview, setOverview] = useState({
    entries_total: 0,
    entries_active: 0,
    documents_total: 0,
    documents_active: 0,
    updated_at: null,
  });

  const [entries, setEntries] = useState([]);
  const [entrySearch, setEntrySearch] = useState('');
  const [entryTypeFilter, setEntryTypeFilter] = useState('');
  const [entrySourceFilter, setEntrySourceFilter] = useState('');

  const [documents, setDocuments] = useState([]);
  const [docSearch, setDocSearch] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [entryModalMode, setEntryModalMode] = useState('create');
  const [entryForm, setEntryForm] = useState({
    id: null,
    title: '',
    content: '',
    entry_type: 'base_n1',
    source_type: 'manual',
    tags_text: '',
    is_active: true,
  });
  const [entrySaving, setEntrySaving] = useState(false);

  const [deleteEntryTarget, setDeleteEntryTarget] = useState(null);
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docFiles, setDocFiles] = useState([]);
  const [docSourceType, setDocSourceType] = useState('documentacao');
  const [docTagsText, setDocTagsText] = useState('');
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [docSummary, setDocSummary] = useState(null);

  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    ticket_number: '',
    title: '',
    use_ai: true,
    source_type: 'ia',
    entry_type: 'runbook',
    tags_text: '',
  });
  const [ticketCreating, setTicketCreating] = useState(false);

  const clearAlerts = () => {
    setMessage('');
    setError('');
  };

  const loadOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/knowledge/overview`);
      setOverview(res.data || {});
    } catch (loadError) {
      setOverview({
        entries_total: 0,
        entries_active: 0,
        documents_total: 0,
        documents_active: 0,
        updated_at: null,
      });
    }
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      setLoadingEntries(true);
      const params = { active: 1 };
      if (entrySearch.trim()) params.q = entrySearch.trim();
      if (entryTypeFilter) params.entry_type = entryTypeFilter;
      if (entrySourceFilter) params.source_type = entrySourceFilter;
      const res = await axios.get(`${apiBaseUrl}/knowledge/entries`, { params });
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (loadError) {
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  }, [entrySearch, entrySourceFilter, entryTypeFilter]);

  const loadDocuments = useCallback(async () => {
    try {
      setLoadingDocuments(true);
      const params = { active: 1 };
      if (docSearch.trim()) params.q = docSearch.trim();
      const res = await axios.get(`${apiBaseUrl}/knowledge/documents`, { params });
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (loadError) {
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [docSearch]);

  const runSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      setLoadingSearch(true);
      const res = await axios.get(`${apiBaseUrl}/knowledge/search`, {
        params: { q: searchTerm.trim(), limit: 18 },
      });
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (searchError) {
      setSearchResults([]);
      setError('Nao foi possivel pesquisar na base de conhecimento.');
    } finally {
      setLoadingSearch(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const entryStats = useMemo(() => {
    const bySource = {};
    entries.forEach((item) => {
      const source = item.source_type || 'manual';
      bySource[source] = (bySource[source] || 0) + 1;
    });
    return bySource;
  }, [entries]);

  const openCreateEntry = () => {
    clearAlerts();
    setEntryModalMode('create');
    setEntryForm({
      id: null,
      title: '',
      content: '',
      entry_type: 'base_n1',
      source_type: 'manual',
      tags_text: '',
      is_active: true,
    });
    setEntryModalOpen(true);
  };

  const openEditEntry = (entry) => {
    clearAlerts();
    setEntryModalMode('edit');
    setEntryForm({
      id: entry.id,
      title: entry.title || '',
      content: entry.content || '',
      entry_type: entry.entry_type || 'base_n1',
      source_type: entry.source_type || 'manual',
      tags_text: (entry.tags || []).join(', '),
      is_active: entry.is_active !== false,
    });
    setEntryModalOpen(true);
  };

  const submitEntry = async () => {
    if (!entryForm.title.trim() || !entryForm.content.trim()) {
      setError('Preencha titulo e conteudo.');
      return;
    }

    clearAlerts();
    setEntrySaving(true);
    try {
      const payload = {
        title: entryForm.title.trim(),
        content: entryForm.content.trim(),
        entry_type: entryForm.entry_type,
        source_type: entryForm.source_type,
        tags: parseTags(entryForm.tags_text),
        is_active: entryForm.is_active,
      };

      if (entryModalMode === 'edit' && entryForm.id) {
        await axios.put(`${apiBaseUrl}/knowledge/entries/${entryForm.id}`, payload);
        setMessage('Artigo atualizado com sucesso.');
      } else {
        await axios.post(`${apiBaseUrl}/knowledge/entries`, payload);
        setMessage('Artigo criado com sucesso.');
      }

      setEntryModalOpen(false);
      await Promise.all([loadEntries(), loadOverview()]);
    } catch (saveError) {
      setError(saveError?.response?.data?.message || 'Falha ao salvar artigo.');
    } finally {
      setEntrySaving(false);
    }
  };

  const confirmDeleteEntry = async () => {
    if (!deleteEntryTarget) return;
    clearAlerts();
    try {
      await axios.delete(`${apiBaseUrl}/knowledge/entries/${deleteEntryTarget.id}`);
      setMessage('Artigo removido com sucesso.');
      setDeleteEntryTarget(null);
      await Promise.all([loadEntries(), loadOverview()]);
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'Falha ao remover artigo.');
    }
  };

  const submitImport = async () => {
    if (!importFile) {
      setError('Selecione um arquivo CSV ou XLSX.');
      return;
    }

    clearAlerts();
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(`${apiBaseUrl}/knowledge/import`, formData);
      setImportSummary(res.data?.summary || null);
      setMessage('Importacao concluida.');
      await Promise.all([loadEntries(), loadOverview()]);
    } catch (importError) {
      setError(importError?.response?.data?.message || 'Falha na importacao da base de conhecimento.');
    } finally {
      setImporting(false);
    }
  };

  const submitDocuments = async () => {
    if (!docFiles.length) {
      setError('Selecione ao menos um arquivo.');
      return;
    }

    clearAlerts();
    setUploadingDocs(true);
    try {
      const formData = new FormData();
      docFiles.forEach((file) => formData.append('files', file));
      formData.append('source_type', docSourceType);
      formData.append('tags', docTagsText);
      const res = await axios.post(`${apiBaseUrl}/knowledge/documents/upload`, formData);
      setDocSummary(res.data?.summary || null);
      setMessage('Documentacao enviada com sucesso.');
      await Promise.all([loadDocuments(), loadOverview()]);
    } catch (uploadError) {
      setError(uploadError?.response?.data?.message || 'Falha ao enviar documentos.');
    } finally {
      setUploadingDocs(false);
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deleteDocTarget) return;
    clearAlerts();
    try {
      await axios.delete(`${apiBaseUrl}/knowledge/documents/${deleteDocTarget.id}`);
      setMessage('Documento removido com sucesso.');
      setDeleteDocTarget(null);
      await Promise.all([loadDocuments(), loadOverview()]);
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'Falha ao remover documento.');
    }
  };

  const submitTicketEntry = async () => {
    if (!ticketForm.ticket_number.trim()) {
      setError('Informe o numero do ticket.');
      return;
    }

    clearAlerts();
    setTicketCreating(true);
    try {
      const payload = {
        ticket_number: ticketForm.ticket_number.trim(),
        title: ticketForm.title.trim(),
        use_ai: ticketForm.use_ai,
        source_type: ticketForm.source_type,
        entry_type: ticketForm.entry_type,
        tags: parseTags(ticketForm.tags_text),
      };
      const res = await axios.post(`${apiBaseUrl}/knowledge/entries/from-ticket`, payload);
      const aiUsed = Boolean(res.data?.ai_used);
      setMessage(aiUsed ? 'Artigo gerado via IA com sucesso.' : 'Artigo criado com transcricao do ticket.');
      setTicketModalOpen(false);
      await Promise.all([loadEntries(), loadOverview()]);
    } catch (ticketError) {
      setError(ticketError?.response?.data?.message || 'Falha ao gerar artigo a partir do ticket.');
    } finally {
      setTicketCreating(false);
    }
  };

  return (
    <div className="knowledge-shell">
      <header className="knowledge-header">
        <div>
          <h2>Base de Conhecimento</h2>
          <p>Centralize a documentacao de atendimento N1 para time e IA com importacao em lote.</p>
        </div>
        <div className="knowledge-kpis">
          <article>
            <span>Artigos ativos</span>
            <strong>{overview.entries_active || 0}</strong>
          </article>
          <article>
            <span>Documentos ativos</span>
            <strong>{overview.documents_active || 0}</strong>
          </article>
          <article>
            <span>Ultima atualizacao</span>
            <strong>{formatDate(overview.updated_at)}</strong>
          </article>
        </div>
      </header>

      {message && <div className="knowledge-feedback is-success">{message}</div>}
      {error && <div className="knowledge-feedback is-error">{error}</div>}

      <div className="knowledge-layout">
        <aside className="knowledge-sidebar">
          {VIEW_OPTIONS.map((view) => (
            <button
              key={view.id}
              type="button"
              className={activeView === view.id ? 'active' : ''}
              onClick={() => setActiveView(view.id)}
            >
              {view.label}
            </button>
          ))}
          <div className="knowledge-sidebar__stats">
            <small>Distribuicao dos artigos</small>
            {Object.keys(entryStats).length === 0 && <span>Sem dados</span>}
            {Object.entries(entryStats).map(([key, total]) => (
              <span key={key}>{key}: {total}</span>
            ))}
          </div>
        </aside>

        <section className="knowledge-content">
          {activeView === 'artigos' && (
            <>
              <div className="knowledge-toolbar">
                <input
                  type="text"
                  placeholder="Buscar por titulo, conteudo ou tags"
                  value={entrySearch}
                  onChange={(event) => setEntrySearch(event.target.value)}
                />
                <select value={entryTypeFilter} onChange={(event) => setEntryTypeFilter(event.target.value)}>
                  <option value="">Todos os tipos</option>
                  {ENTRY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select value={entrySourceFilter} onChange={(event) => setEntrySourceFilter(event.target.value)}>
                  <option value="">Todas as origens</option>
                  {SOURCE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <button type="button" className="ghost" onClick={() => setImportModalOpen(true)}>Importar CSV/XLSX</button>
                <button type="button" className="ghost" onClick={() => setTicketModalOpen(true)}>Gerar de Ticket</button>
                <button type="button" onClick={openCreateEntry}>Novo artigo</button>
              </div>

              <div className="knowledge-list">
                {loadingEntries && <p>Carregando artigos...</p>}
                {!loadingEntries && entries.length === 0 && <p>Sem artigos cadastrados.</p>}
                {!loadingEntries && entries.map((entry) => (
                  <article key={entry.id} className="knowledge-card">
                    <header>
                      <div>
                        <h3>{entry.title}</h3>
                        <p>{entry.entry_type} • {entry.source_type} • {formatDate(entry.updated_at)}</p>
                      </div>
                      <div className="knowledge-card__actions">
                        <button type="button" className="ghost" onClick={() => openEditEntry(entry)}>Editar</button>
                        <button type="button" className="ghost danger" onClick={() => setDeleteEntryTarget(entry)}>Excluir</button>
                      </div>
                    </header>
                    <p className="knowledge-card__content">{entry.content}</p>
                    <footer>
                      {(entry.tags || []).map((tag) => (
                        <span key={`${entry.id}-${tag}`}>{tag}</span>
                      ))}
                    </footer>
                  </article>
                ))}
              </div>
            </>
          )}

          {activeView === 'documentos' && (
            <>
              <div className="knowledge-toolbar">
                <input
                  type="text"
                  placeholder="Buscar documento"
                  value={docSearch}
                  onChange={(event) => setDocSearch(event.target.value)}
                />
                <button type="button" onClick={() => setDocModalOpen(true)}>Nova carga de documentacao</button>
              </div>

              <div className="knowledge-list">
                {loadingDocuments && <p>Carregando documentos...</p>}
                {!loadingDocuments && documents.length === 0 && <p>Sem documentos cadastrados.</p>}
                {!loadingDocuments && documents.map((doc) => (
                  <article key={doc.id} className="knowledge-card">
                    <header>
                      <div>
                        <h3>{doc.file_name}</h3>
                        <p>{doc.file_extension || '-'} • {doc.source_type || 'documentacao'} • {formatDate(doc.updated_at)}</p>
                      </div>
                      <div className="knowledge-card__actions">
                        <button type="button" className="ghost danger" onClick={() => setDeleteDocTarget(doc)}>Excluir</button>
                      </div>
                    </header>
                    <p className="knowledge-card__content">{doc.content_text}</p>
                    <footer>
                      {(doc.tags || []).map((tag) => (
                        <span key={`${doc.id}-${tag}`}>{tag}</span>
                      ))}
                    </footer>
                  </article>
                ))}
              </div>
            </>
          )}

          {activeView === 'busca' && (
            <div className="knowledge-search-view">
              <div className="knowledge-toolbar">
                <input
                  type="text"
                  placeholder="Pergunte algo para consultar a base de conhecimento"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <button type="button" onClick={runSearch} disabled={loadingSearch || !searchTerm.trim()}>
                  {loadingSearch ? 'Pesquisando...' : 'Pesquisar'}
                </button>
              </div>

              <div className="knowledge-list">
                {!loadingSearch && searchResults.length === 0 && <p>Sem resultados no momento.</p>}
                {searchResults.map((item) => (
                  <article key={`${item.type}-${item.id}`} className="knowledge-card">
                    <header>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.type === 'document' ? 'Documento' : 'Artigo'} • score {item.score ?? 0}</p>
                      </div>
                    </header>
                    <p className="knowledge-card__content">{item.snippet}</p>
                    <footer>
                      {(item.tags || []).map((tag) => (
                        <span key={`${item.id}-${tag}`}>{tag}</span>
                      ))}
                    </footer>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      <AppModal
        open={entryModalOpen}
        title={entryModalMode === 'edit' ? 'Editar artigo' : 'Novo artigo'}
        description="Documente conhecimento operacional para acelerar o atendimento de nivel 1."
        confirmText={entrySaving ? 'Salvando...' : 'Salvar artigo'}
        onConfirm={submitEntry}
        onCancel={() => setEntryModalOpen(false)}
        disableConfirm={entrySaving}
        loading={entrySaving}
      >
        <div className="knowledge-modal-grid">
          <label>
            <span>Titulo</span>
            <input
              type="text"
              value={entryForm.title}
              onChange={(event) => setEntryForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label>
            <span>Tipo</span>
            <select
              value={entryForm.entry_type}
              onChange={(event) => setEntryForm((current) => ({ ...current, entry_type: event.target.value }))}
            >
              {ENTRY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Origem</span>
            <select
              value={entryForm.source_type}
              onChange={(event) => setEntryForm((current) => ({ ...current, source_type: event.target.value }))}
            >
              {SOURCE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Tags (separadas por virgula)</span>
            <input
              type="text"
              value={entryForm.tags_text}
              onChange={(event) => setEntryForm((current) => ({ ...current, tags_text: event.target.value }))}
            />
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={entryForm.is_active}
              onChange={(event) => setEntryForm((current) => ({ ...current, is_active: event.target.checked }))}
            />
            <span>Artigo ativo</span>
          </label>
          <label className="content-field">
            <span>Conteudo</span>
            <textarea
              rows={8}
              value={entryForm.content}
              onChange={(event) => setEntryForm((current) => ({ ...current, content: event.target.value }))}
            />
          </label>
        </div>
      </AppModal>

      <AppModal
        open={importModalOpen}
        title="Importar base de conhecimento"
        description="Envie um arquivo CSV ou XLSX com colunas title/content e opcionalmente tipo, tags e origem."
        confirmText={importing ? 'Importando...' : 'Importar agora'}
        onConfirm={submitImport}
        onCancel={() => setImportModalOpen(false)}
        disableConfirm={importing || !importFile}
        loading={importing}
      >
        <div className="knowledge-modal-grid">
          <label>
            <span>Arquivo</span>
            <input type="file" accept=".csv,.xlsx" onChange={(event) => setImportFile(event.target.files?.[0] || null)} />
          </label>
          {importSummary && (
            <div className="knowledge-summary">
              <span>Total: {importSummary.total || 0}</span>
              <span>Criados: {importSummary.created || 0}</span>
              <span>Atualizados: {importSummary.updated || 0}</span>
              <span>Pulados: {importSummary.skipped || 0}</span>
            </div>
          )}
        </div>
      </AppModal>

      <AppModal
        open={docModalOpen}
        title="Carga de documentacao"
        description="Anexe TXT, MD, JSON, CSV ou XLSX para ampliar a base usada no atendimento e na IA."
        confirmText={uploadingDocs ? 'Enviando...' : 'Enviar documentos'}
        onConfirm={submitDocuments}
        onCancel={() => setDocModalOpen(false)}
        disableConfirm={uploadingDocs || !docFiles.length}
        loading={uploadingDocs}
      >
        <div className="knowledge-modal-grid">
          <label>
            <span>Arquivos</span>
            <input
              type="file"
              multiple
              accept=".txt,.md,.log,.json,.csv,.xlsx"
              onChange={(event) => setDocFiles(Array.from(event.target.files || []))}
            />
          </label>
          <label>
            <span>Origem</span>
            <select value={docSourceType} onChange={(event) => setDocSourceType(event.target.value)}>
              {SOURCE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Tags (separadas por virgula)</span>
            <input type="text" value={docTagsText} onChange={(event) => setDocTagsText(event.target.value)} />
          </label>
          {docSummary && (
            <div className="knowledge-summary">
              <span>Total: {docSummary.total || 0}</span>
              <span>Importados: {docSummary.created || 0}</span>
              <span>Pulados: {docSummary.skipped || 0}</span>
            </div>
          )}
        </div>
      </AppModal>

      <AppModal
        open={ticketModalOpen}
        title="Gerar artigo por ticket"
        description="Crie automaticamente um artigo a partir da conversa de um ticket (com ou sem IA)."
        confirmText={ticketCreating ? 'Gerando...' : 'Gerar artigo'}
        onConfirm={submitTicketEntry}
        onCancel={() => setTicketModalOpen(false)}
        disableConfirm={ticketCreating || !ticketForm.ticket_number.trim()}
        loading={ticketCreating}
      >
        <div className="knowledge-modal-grid">
          <label>
            <span>Ticket</span>
            <input
              type="text"
              placeholder="Ex.: TIC000123"
              value={ticketForm.ticket_number}
              onChange={(event) => setTicketForm((current) => ({ ...current, ticket_number: event.target.value }))}
            />
          </label>
          <label>
            <span>Titulo (opcional)</span>
            <input
              type="text"
              value={ticketForm.title}
              onChange={(event) => setTicketForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label>
            <span>Tipo</span>
            <select
              value={ticketForm.entry_type}
              onChange={(event) => setTicketForm((current) => ({ ...current, entry_type: event.target.value }))}
            >
              {ENTRY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Origem</span>
            <select
              value={ticketForm.source_type}
              onChange={(event) => setTicketForm((current) => ({ ...current, source_type: event.target.value }))}
            >
              {SOURCE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Tags</span>
            <input
              type="text"
              value={ticketForm.tags_text}
              onChange={(event) => setTicketForm((current) => ({ ...current, tags_text: event.target.value }))}
            />
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={ticketForm.use_ai}
              onChange={(event) =>
                setTicketForm((current) => ({
                  ...current,
                  use_ai: event.target.checked,
                  source_type: event.target.checked ? 'ia' : current.source_type,
                }))
              }
            />
            <span>Usar IA para resumir a conversa</span>
          </label>
        </div>
      </AppModal>

      <AppModal
        open={Boolean(deleteEntryTarget)}
        title="Excluir artigo"
        description="Esta acao remove o artigo permanentemente da base."
        confirmText="Excluir"
        onConfirm={confirmDeleteEntry}
        onCancel={() => setDeleteEntryTarget(null)}
        variant="danger"
      >
        <p>Deseja excluir o artigo <strong>{deleteEntryTarget?.title}</strong>?</p>
      </AppModal>

      <AppModal
        open={Boolean(deleteDocTarget)}
        title="Excluir documento"
        description="Esta acao remove o documento permanentemente da base."
        confirmText="Excluir"
        onConfirm={confirmDeleteDocument}
        onCancel={() => setDeleteDocTarget(null)}
        variant="danger"
      >
        <p>Deseja excluir o documento <strong>{deleteDocTarget?.file_name}</strong>?</p>
      </AppModal>
    </div>
  );
};

export default KnowledgeManager;
